// Each scrambled character is an inline-block, and a line may break between
// inline-blocks. Left alone, a word can wrap mid-scramble where the settled
// text cannot, so the heading jumps when it settles. Grouping each word's
// characters (`.scramble-word` is white-space: nowrap) keeps both states
// wrapping the same way. `ch` is the underlying character, `html` what to emit.
const groupWords = (pieces) => {
	let output = '';
	let word = '';
	const flush = () => {
		if (word) output += `<span class="scramble-word">${word}</span>`;
		word = '';
	};
	for (const { ch, html } of pieces) {
		if (ch === ' ') {
			flush();
			output += html;
		} else {
			word += html;
		}
	}
	flush();
	return output;
};

class TextScramble {
	constructor(el) {
		this.el = el;
		// Softer character pool — rounder/gentler glyphs that share visual weight
		// with letters, no jagged brackets or carets. Underscores repeat to keep
		// some "settled-looking" frames in the mix.
		this.chars = ':;~<>//||{}[]---$=+';
		this.update = this.update.bind(this);
	}

	// `fromDuds` — the element currently shows the glyphs preScramble put there,
	// so characters that have not started resolving yet keep their dud styling
	// instead of popping into plain text for a few frames.
	setText(newText, fromDuds = false) {
		const oldText = this.el.textContent;
		const length = Math.max(oldText.length, newText.length);
		const promise = new Promise((resolve) => (this.resolve = resolve));
		this.queue = [];
		this.fromDuds = fromDuds;

		for (let i = 0; i < length; i++) {
			const from = oldText[i] || '';
			const to = newText[i] || '';
			const start = Math.floor(Math.random() * 40);
			const end = start + Math.floor(Math.random() * 40);
			this.queue.push({ from, to, start, end });
		}

		cancelAnimationFrame(this.frameRequest);
		this.frame = 0;
		this.update();
		return promise;
	}

	update() {
		const pieces = [];
		let complete = 0;

		for (let i = 0, n = this.queue.length; i < n; i++) {
			let { from, to, start, end, char } = this.queue[i];

			if (this.frame >= end) {
				complete++;
				pieces.push({ ch: to, html: to });
			} else if (this.frame >= start) {
				// Lower re-roll probability → each random glyph lingers longer,
				// reading as a calmer pulse rather than a flicker.
				if (!char || Math.random() < 0.15) {
					char = this.randomChar();
					this.queue[i].char = char;
				}
				pieces.push({ ch: to || from, html: `<span class="dud">${char}</span>` });
			} else if (this.fromDuds && from !== ' ') {
				pieces.push({ ch: from, html: `<span class="dud">${from}</span>` });
			} else {
				pieces.push({ ch: from, html: from });
			}
		}

		this.el.innerHTML = groupWords(pieces);

		if (complete === this.queue.length) {
			this.resolve();
		} else {
			this.frameRequest = requestAnimationFrame(this.update);
			this.frame++;
		}
	}

	randomChar() {
		return this.chars[Math.floor(Math.random() * this.chars.length)];
	}
}

const markStarted = (el) => {
	if (el.dataset.scrambleStarted) return false;
	el.dataset.scrambleStarted = '1';
	return true;
};

const SCRAMBLE_CHARS = '.:;,~-=+/*•·°____';
const randomChar = () => SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];

const preScramble = (el) => {
	if (el.dataset.scrambleTarget !== undefined) return;
	const target = el.textContent;
	if (!target) return;
	el.dataset.scrambleTarget = target;
	const pieces = [];
	for (let i = 0; i < target.length; i++) {
		const ch = target[i];
		pieces.push({ ch, html: ch === ' ' ? ' ' : `<span class="dud">${randomChar()}</span>` });
	}
	el.innerHTML = groupWords(pieces);
};

export const runScrambleOnce = (el) => {
	if (!el || !markStarted(el)) return;
	const preScrambled = el.dataset.scrambleTarget !== undefined;
	const target = preScrambled ? el.dataset.scrambleTarget : el.textContent;
	if (!target) return;
	const fx = new TextScramble(el);
	fx.setText(target, preScrambled);
};

const pinWidthToWidestPhrase = (el, phrases) => {
	if (phrases.length <= 1) return;
	const originalHTML = el.innerHTML;
	let maxWidth = 0;

	const measure = () => {
		const w = el.getBoundingClientRect().width;
		if (w > maxWidth) maxWidth = w;
	};

	for (const phrase of phrases) {
		// Settled state (plain characters)
		el.textContent = phrase;
		measure();

		// Mid-scramble state (every char as a dud span) — wider because the
		// parent's letter-spacing doesn't fully apply between inline-block spans
		el.innerHTML = groupWords([...phrase].map(ch => ({ ch, html: ch === ' ' ? ' ' : `<span class="dud">${ch}</span>` })));
		measure();
	}

	el.innerHTML = originalHTML;
	el.style.display = 'inline-block';
	el.style.minWidth = `${Math.ceil(maxWidth)}px`;
	el.style.textAlign = 'center';
};

export const runScrambleCycle = (el, phrases) => {
	if (!el || !markStarted(el)) return;
	// The word the markup carries is what preScramble hid, so read it back from
	// there — innerText would be the placeholder glyphs by now.
	const preScrambled = el.dataset.scrambleTarget !== undefined;
	const baseText = preScrambled ? el.dataset.scrambleTarget : el.innerText;
	const list = phrases.includes(baseText) ? phrases : [baseText, ...phrases];

	// Lock the rendered width to the widest phrase so layout doesn't reflow on every cycle
	pinWidthToWidestPhrase(el, list);

	const fx = new TextScramble(el);
	// Start on the markup word wherever the editor placed it in the list, so the
	// first pass is a reveal of that word, and only later passes swap it out.
	let counter = Math.max(0, list.indexOf(baseText));
	let first = true;

	const next = () => {
		fx.setText(list[counter], first && preScrambled).then(() => {
			setTimeout(next, 2000);
		});
		first = false;
		counter = (counter + 1) % list.length;
	};

	next();
};

export const parseScramblePhrases = (wrapper) => {
	if (!wrapper || !wrapper.dataset.scrambleWords) return null;
	try {
		const phrases = JSON.parse(wrapper.dataset.scrambleWords);
		return Array.isArray(phrases) && phrases.length > 0 ? phrases : null;
	} catch (e) {
		return null;
	}
};

// The hero's cycling <del> starts out as placeholder glyphs like the one-shot
// ones, so its first pass reveals the word instead of scrambling the word into
// itself. The hero timeline kicks the cycle off once the heading has faded in.
export const tagHeroScrambleDel = (heroEl) => {
	const phrases = parseScramblePhrases(heroEl);
	if (!phrases) return null;
	const del = heroEl.querySelector('del');
	if (!del) return null;
	del.classList.add('scramble-cycle');
	preScramble(del);
	return { del, phrases };
};

const initOneShotObserver = () => {
	const targets = [...document.querySelectorAll('del')].filter(
		(el) => !el.classList.contains('scramble-cycle')
	);
	if (!targets.length) return;

	targets.forEach(preScramble);

	if (!('IntersectionObserver' in window)) {
		targets.forEach(runScrambleOnce);
		return;
	}

	const observer = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				runScrambleOnce(entry.target);
				observer.unobserve(entry.target);
			}
		});
	}, { threshold: 0, rootMargin: '0px 0px -10% 0px' });

	targets.forEach((el) => observer.observe(el));
};

const init = () => {
	document.querySelectorAll('section.hero[data-scramble-words]').forEach(tagHeroScrambleDel);
	initOneShotObserver();
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', init);
} else {
	init();
}
